import { Directive, ElementRef, Injector, Input, Renderer2 } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatSlider } from '@angular/material/slider';

/**
 * For use when you want to disable any element (ex. input, mat-form-field) at the HTML level, but not at the formControl level
 * Using [disabled] attribute with ReactiveForms throws console warnings, and formControl.disable() removes validations & value from form
 *
 * @export
 * @class ElementDisabledDirective
 */
@Directive({
  selector: '[elementDisabled]',
  standalone: false,
})
export class ElementDisabledDirective {
  private _disabled = false;
  private _element: HTMLElement;

  constructor(
    private elementRef: ElementRef,
    private injector: Injector,
    private renderer: Renderer2
  ) {
    this._element = this.elementRef.nativeElement;
  }

  @Input()
  set elementDisabled(disabled: boolean) {
    if (disabled !== undefined) {
      this._disabled = disabled;

      // setTimeout hack to allow any possible Material components/directive to instantiate
      // this allows us to set the disabled attribute without the component overwriting it at instantiation
      setTimeout(() => {
        this.handleElement(this._element);
      });
    }
  }

  /**
   * Handles element based on it's tagName (type)
   *
   * @param {HTMLElement} element
   */
  private handleElement(element: HTMLElement) {
    switch (element.tagName) {
      case 'INPUT': {
        const matFormField = element.closest('mat-form-field') as HTMLElement;
        if (matFormField) {
          // if directive is used with an <input> element we use its parent <mat-form-field> so we can apply appropriate material css classes for disabled
          this.toggleMatFormFieldDisable(matFormField);
        } else {
          // no parent <mat-form-field>
          this.toggleInputDisable(element as HTMLInputElement);
        }
        break;
      }
      case 'MAT-FORM-FIELD':
        this.toggleMatFormFieldDisable(element);
        break;
      case 'MAT-SELECT':
        this.toggleMatComponentDisable(this.injector.get(MatSelect));
        break;
      case 'MAT-SLIDER':
        this.toggleMatComponentDisable(this.injector.get(MatSlider));
        break;
      default:
        this.toggleDefaultDisable(element);
    }
  }
  /**
   * Handles <input> element
   *
   * @param {HTMLInputElement} element
   */
  private toggleInputDisable(element: HTMLInputElement) {
    if (this._disabled) {
      this.renderer.setAttribute(element, 'disabled', 'true');
    } else {
      this.renderer.removeAttribute(element, 'disabled');
    }
  }

  /**
   * Handles any <input> element inside given element
   *
   * @param {HTMLElement} element
   */
  private toggleChildInputDisable(element: HTMLElement) {
    element.querySelectorAll('input').forEach((input) => {
      this.toggleInputDisable(input);
    });
  }

  /**
   *  Handles <mat-form-field> element
   *
   * @param {HTMLElement} element
   */
  private toggleMatFormFieldDisable(element: HTMLElement) {
    if (this._disabled) {
      this.renderer.addClass(element, 'mat-form-field-disabled');
    } else {
      this.renderer.removeClass(element, 'mat-form-field-disabled');
    }
    this.toggleChildInputDisable(element);
  }

  /**
   * Handles <mat-select> or <mat-slider> elements
   *
   * @param {MatSelect | MatSlider} element
   */
  private toggleMatComponentDisable(element: MatSelect | MatSlider) {
    if (element instanceof MatSelect) {
      element.setDisabledState(this._disabled);
    } else if (element instanceof MatSlider) {
      element.disabled = this._disabled;
    }
  }

  /**
   * Handles any element that isn't <input> or <mat-form-field>
   *
   * @param {HTMLElement} element
   */
  private toggleDefaultDisable(element: HTMLElement) {
    if (this._disabled) {
      this.renderer.setStyle(element, 'pointer-events', 'none');
      this.renderer.setStyle(element, 'opacity', 0.38);
    } else {
      this.renderer.removeStyle(element, 'pointer-events');
      this.renderer.removeStyle(element, 'opacity');
    }
    this.toggleChildInputDisable(element);
  }
}
